"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import embla_carousel_autoplay from "embla-carousel-autoplay";
import embla_carousel_fade from "embla-carousel-fade";
import embla_carousel_react from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { useReducedMotion } from "motion/react";
import { useCallback, useEffect, useId, useMemo, useRef } from "react";
import { useDetectTouchDevice, useSafeInView } from "../../hooks/index.js";
import { cn } from "../../utils/cn.js";
import { Image } from "../Image/index.js";
import { carouselVariants } from "./Carousel.variants.js";
import { useCarouselAutoplayWithContext } from "./CarouselAutoplayContextHook.js";
import { useCarouselAutoplay } from "./CarouselAutoplayHook.js";
import { CarouselControls } from "./CarouselControls.js";
import { useCarouselDotButton } from "./CarouselDotButtonHooks.js";
import { normalizePlugins } from "./CarouselPluginUtils.js";
import { useCarouselPrevNextButtons } from "./CarouselPrevNextButtonHooks.js";
function getImageSlideProps(isSelectedSlide) {
    return {
        inertProps: isSelectedSlide ? {} : {
            inert: ''
        },
        tabIndexProps: isSelectedSlide ? {
            tabIndex: 0
        } : {}
    };
}
function renderCarouselImage({ imageSrc, imageClassName, alt, objectFit = 'cover', lazyLoad = false, lazyRootMargin = '200px', lazyViewportRef, lazyForceLoad = false }) {
    return /*#__PURE__*/ jsx(Image, {
        src: imageSrc,
        className: imageClassName,
        alt: alt,
        objectFit: objectFit,
        width: "full",
        height: "full",
        objectPosition: "center",
        lazyLoad: lazyLoad,
        lazyRootMargin: lazyRootMargin,
        lazyViewportRef: lazyViewportRef,
        lazyForceLoad: lazyForceLoad
    });
}
function renderSlide({ slide, index, slideClasses, isImageCarousel, isSelectedSlide, minSlideWidth, slideWidth, lazyLoad, lazyLoadRootMargin, viewportRef }) {
    if (isImageCarousel) {
        const imageSlide = slide;
        const alt = imageSlide.title ?? `Slide ${index + 1}`;
        const ariaLabel = imageSlide.title ?? `Slide ${index + 1}`;
        const { inertProps, tabIndexProps } = getImageSlideProps(isSelectedSlide);
        return /*#__PURE__*/ jsx("li", {
            className: slideClasses,
            "aria-hidden": !isSelectedSlide,
            ...inertProps,
            ...tabIndexProps,
            "aria-label": ariaLabel,
            children: renderCarouselImage({
                imageSrc: imageSlide.image,
                imageClassName: imageSlide.imageClassName,
                alt,
                objectFit: 'cover',
                lazyLoad,
                lazyRootMargin: lazyLoadRootMargin,
                lazyViewportRef: viewportRef,
                lazyForceLoad: isSelectedSlide
            })
        }, slide.id);
    }
    const contentSlide = slide;
    const customMinWidthStyle = 'min' === slideWidth && minSlideWidth ? {
        minWidth: minSlideWidth
    } : {};
    return /*#__PURE__*/ jsx("li", {
        className: slideClasses,
        style: customMinWidthStyle,
        "aria-label": contentSlide.title ?? `Slide ${index + 1}`,
        children: contentSlide.childrenNodes
    }, slide.id);
}
function Carousel(props) {
    const { slotClassNames = {}, slides, ariaLabel, ...restProps } = props;
    const lazyLoad = 'lazyLoad' in props ? props.lazyLoad ?? false : false;
    const lazyLoadRootMargin = 'lazyLoadRootMargin' in props ? props.lazyLoadRootMargin ?? '200px' : '200px';
    const generatedId = useId();
    const isTouchDevice = useDetectTouchDevice();
    const prefersReducedMotion = useReducedMotion() ?? false;
    const isImageCarousel = 'image' === props.type;
    const { pagination } = props;
    const pluginConfigs = normalizePlugins(isImageCarousel ? props.plugins : void 0);
    const autoplayConfig = pluginConfigs.find((c)=>'autoplay' === c.type);
    const hasAutoplay = isImageCarousel && !!autoplayConfig;
    const autoplayMode = autoplayConfig?.mode;
    const autoplayOverride = autoplayConfig?.override;
    const autoplaySyncGlobally = autoplayConfig?.syncGlobally ?? false;
    const loop = isImageCarousel ? props.loop ?? true : false;
    const carouselId = props.carouselId || generatedId;
    const dragFree = isImageCarousel ? false : props.dragFree ?? false;
    const hideArrowsOnMobileOrTouch = isTouchDevice ? 'always' : 'responsive';
    const slidesToScrollProp = isImageCarousel ? void 0 : props.slidesToScroll;
    const isLightBoxSupported = isImageCarousel && (props.isLightBoxSupported ?? false);
    const showArrowBtns = 'numbers' === pagination;
    const showDotBtns = 'dots' === pagination;
    const showSlideNum = isImageCarousel && 'numbers' === pagination;
    const showBlurBox = isImageCarousel && (showSlideNum || isLightBoxSupported || hasAutoplay);
    const minSlideWidth = 'minSlideWidth' in props ? props.minSlideWidth : void 0;
    const slideWidth = props.slideWidth ?? ('content' === props.type ? 'auto' : 'image');
    const emblaOptions = useMemo(()=>{
        const options = {
            loop
        };
        if (dragFree) options.dragFree = true;
        if (void 0 !== slidesToScrollProp) options.slidesToScroll = slidesToScrollProp;
        return options;
    }, [
        loop,
        dragFree,
        slidesToScrollProp
    ]);
    const pluginsKey = JSON.stringify(pluginConfigs.map((c)=>'autoplay' === c.type ? {
            type: c.type,
            delay: c.delay,
            syncGlobally: c.syncGlobally
        } : c));
    const plugins = useMemo(()=>{
        const configs = JSON.parse(pluginsKey);
        const emblaPlugins = [];
        for (const config of configs)switch(config.type){
            case 'wheelGestures':
                emblaPlugins.push(WheelGesturesPlugin());
                break;
            case 'fade':
                emblaPlugins.push(embla_carousel_fade());
                break;
            case 'autoplay':
                emblaPlugins.push(embla_carousel_autoplay({
                    playOnInit: false,
                    delay: config.delay ?? 5000,
                    stopOnMouseEnter: false,
                    stopOnFocusIn: false,
                    stopOnInteraction: false
                }));
                break;
        }
        return emblaPlugins;
    }, [
        pluginsKey
    ]);
    const [emblaRef, emblaApi] = embla_carousel_react(emblaOptions, plugins);
    const viewportRef = useRef(null);
    const emblaCallbackRef = useCallback((node)=>{
        viewportRef.current = node;
        emblaRef(node);
    }, [
        emblaRef
    ]);
    const { selectedIndex, onDotButtonClick } = useCarouselDotButton(emblaApi);
    const { prevBtnDisabled, nextBtnDisabled, prevBtnHidden, onPrevButtonClick, onNextButtonClick } = useCarouselPrevNextButtons(emblaApi, loop);
    const contextAutoplayOptions = {
        autoplaySyncGlobally,
        carouselId,
        prefersReducedMotion
    };
    const standardAutoplayResult = useCarouselAutoplay(hasAutoplay && !autoplaySyncGlobally ? emblaApi : void 0, prefersReducedMotion);
    const contextAutoplayResult = useCarouselAutoplayWithContext(hasAutoplay && autoplaySyncGlobally ? emblaApi : void 0, contextAutoplayOptions);
    const autoplayResult = autoplaySyncGlobally ? contextAutoplayResult : standardAutoplayResult;
    const { autoplayIsPlaying = false, autoplayIsManuallyPaused = false, hasUserInteracted = false, toggleAutoplay = ()=>{}, resetAutoplayOnInteraction = (cb)=>cb(), isManuallyPausedRef, hasUserInteractedRef, globalAutoplayPaused, globalAutoplayPausedRef } = hasAutoplay ? autoplayResult : {};
    const onLightboxButtonClick = (callback)=>callback();
    const handleMouseEnter = ()=>{
        if (hasAutoplay && 'hover' === autoplayMode && !autoplayIsManuallyPaused && void 0 === autoplayOverride) {
            const autoplay = emblaApi?.plugins()?.autoplay;
            if (autoplay && (!prefersReducedMotion || autoplay.isPlaying())) autoplay.play();
        }
    };
    const handleMouseLeave = ()=>{
        if (hasAutoplay && 'hover' === autoplayMode && void 0 === autoplayOverride) {
            const autoplay = emblaApi?.plugins()?.autoplay;
            if (autoplay) {
                if (prefersReducedMotion && autoplay.isPlaying()) return;
                autoplay.stop();
            }
        }
    };
    const autoplayInViewRef = useRef(null);
    const isInView = useSafeInView(autoplayInViewRef, {
        once: false
    });
    const prevModeRef = useRef(autoplayMode);
    useEffect(()=>{
        if (!hasAutoplay) return;
        if (prevModeRef.current !== autoplayMode) {
            const autoplay = emblaApi?.plugins()?.autoplay;
            if (autoplay?.isPlaying()) autoplay.stop();
        }
        prevModeRef.current = autoplayMode;
    }, [
        emblaApi,
        hasAutoplay,
        autoplayMode
    ]);
    useEffect(()=>{
        if ('inview' !== autoplayMode) return;
        if (void 0 !== autoplayOverride) return;
        if (!emblaApi) return;
        const autoplay = emblaApi.plugins()?.autoplay;
        if (!autoplay) return;
        const applyInviewState = ()=>{
            const ap = emblaApi.plugins()?.autoplay;
            if (!ap) return;
            if (prefersReducedMotion) ap.stop();
            else if (isInView && !autoplayIsManuallyPaused) ap.play();
            else ap.stop();
        };
        if (emblaApi.slidesInView().length > 0) applyInviewState();
        else emblaApi.on('init', applyInviewState);
        return ()=>{
            emblaApi.off('init', applyInviewState);
            emblaApi.plugins()?.autoplay?.stop();
        };
    }, [
        emblaApi,
        isInView,
        autoplayMode,
        autoplayIsManuallyPaused,
        prefersReducedMotion,
        autoplayOverride
    ]);
    useEffect(()=>{
        if (void 0 === autoplayOverride) return;
        if (!emblaApi) return;
        const autoplay = emblaApi.plugins()?.autoplay;
        if (!autoplay) return;
        if (prefersReducedMotion) {
            autoplay.stop();
            return ()=>{
                emblaApi.plugins()?.autoplay?.stop();
            };
        }
        const startOrStopAutoplay = ()=>{
            const ap = emblaApi.plugins()?.autoplay;
            if (!ap) return;
            const isGloballyPaused = globalAutoplayPausedRef?.current || globalAutoplayPaused || false;
            const isLocallyPaused = isManuallyPausedRef?.current || autoplayIsManuallyPaused;
            const userHasInteracted = hasUserInteractedRef?.current || hasUserInteracted;
            if (autoplayOverride) if (isGloballyPaused || isLocallyPaused) {
                if (ap.isPlaying()) ap.stop();
            } else ap.play();
            else if (userHasInteracted) {
                if (isLocallyPaused) ap.stop();
            } else ap.stop();
        };
        startOrStopAutoplay();
        emblaApi.on('init', startOrStopAutoplay);
        return ()=>{
            emblaApi.off('init', startOrStopAutoplay);
        };
    }, [
        emblaApi,
        autoplayOverride,
        autoplayIsManuallyPaused,
        hasUserInteracted,
        prefersReducedMotion,
        isManuallyPausedRef,
        hasUserInteractedRef,
        globalAutoplayPaused,
        globalAutoplayPausedRef
    ]);
    useEffect(()=>{
        if ('init' !== autoplayMode) return;
        if (void 0 !== autoplayOverride) return;
        if (prefersReducedMotion) return;
        if (autoplayIsManuallyPaused) return;
        if (!emblaApi) return;
        const startAutoplay = ()=>{
            const autoplay = emblaApi.plugins()?.autoplay;
            if (autoplay) autoplay.play();
        };
        if (emblaApi.slidesInView().length > 0) startAutoplay();
        else emblaApi.on('init', startAutoplay);
        return ()=>{
            emblaApi.off('init', startAutoplay);
            emblaApi.plugins()?.autoplay?.stop();
        };
    }, [
        emblaApi,
        autoplayMode,
        autoplayOverride,
        prefersReducedMotion,
        autoplayIsManuallyPaused
    ]);
    useEffect(()=>{
        if (prefersReducedMotion) {
            const autoplay = emblaApi?.plugins()?.autoplay;
            if (autoplay?.isPlaying()) autoplay.stop();
        }
    }, [
        emblaApi,
        prefersReducedMotion
    ]);
    const { wrapperParentClass = '', wrapperClass = '', rootClass = '', viewportClass = '', containerClass = '', slideClass = '', buttonClass = '', dotsPaginationClass = '', dotButtonClass = '', dotClass = '', blurBoxClass = '', blurBoxDividerClass = '', blurBoxNumbersClass = '', blurBoxLightboxButtonClass = '', blurBoxAutoplayButtonClass = '' } = slotClassNames;
    const { wrapperParentSlot, wrapperSlot, rootSlot, viewportSlot, containerSlot, prevButtonSlot, nextButtonSlot, hiddenButtonSlot, dotsPaginationSlot, dotButtonSlot, slideSlot } = carouselVariants({
        ...restProps,
        type: isImageCarousel ? 'image' : 'content',
        loop,
        hideArrowsOnMobileOrTouch: hideArrowsOnMobileOrTouch,
        bleedSpacing: restProps.bleedSpacing
    });
    const wrapperParentClasses = cn(wrapperParentSlot(), wrapperParentClass);
    const wrapperClasses = cn(wrapperSlot(), wrapperClass);
    const rootClasses = cn(rootSlot(), rootClass);
    const viewportClasses = cn(viewportSlot(), viewportClass);
    const containerClasses = cn(containerSlot(), containerClass);
    const slideClasses = cn(slideSlot(), slideClass);
    const currentSlide = selectedIndex + 1;
    const totalSlides = slides?.length ?? 0;
    const onPrevNextButtonClick = useCallback((callback)=>resetAutoplayOnInteraction(callback), [
        resetAutoplayOnInteraction
    ]);
    const controlsConfig = {
        showArrowBtns,
        showDotBtns,
        showBlurBox,
        showSlideNum,
        isLightBoxSupported,
        hasAutoplay,
        isImageCarousel
    };
    return /*#__PURE__*/ jsx("div", {
        "data-testid": "carousel-wrapper-parent",
        className: wrapperParentClasses,
        children: /*#__PURE__*/ jsx("div", {
            "data-testid": "carousel-wrapper",
            className: wrapperClasses,
            children: /*#__PURE__*/ jsxs("section", {
                "aria-label": ariaLabel,
                "data-testid": "carousel-root",
                className: rootClasses,
                onMouseEnter: handleMouseEnter,
                onMouseLeave: handleMouseLeave,
                ref: autoplayInViewRef,
                children: [
                    /*#__PURE__*/ jsx("div", {
                        "data-testid": "carousel-viewport",
                        className: viewportClasses,
                        ref: emblaCallbackRef,
                        children: /*#__PURE__*/ jsx("ul", {
                            "data-testid": "carousel-container",
                            className: containerClasses,
                            children: slides?.map((slide, index)=>{
                                const isSelectedSlide = index === selectedIndex;
                                return renderSlide({
                                    slide,
                                    index,
                                    slideClasses,
                                    isImageCarousel,
                                    isSelectedSlide,
                                    minSlideWidth,
                                    slideWidth,
                                    lazyLoad,
                                    lazyLoadRootMargin,
                                    viewportRef
                                });
                            })
                        })
                    }),
                    /*#__PURE__*/ jsx(CarouselControls, {
                        config: controlsConfig,
                        slides: slides,
                        selectedIndex: selectedIndex,
                        prevBtnDisabled: prevBtnDisabled,
                        nextBtnDisabled: nextBtnDisabled,
                        prevBtnHidden: prevBtnHidden,
                        onPrevButtonClick: onPrevButtonClick,
                        onNextButtonClick: onNextButtonClick,
                        onDotButtonClick: onDotButtonClick,
                        onPrevNextButtonClick: onPrevNextButtonClick,
                        resetAutoplayOnInteraction: resetAutoplayOnInteraction,
                        onLightboxButtonClick: onLightboxButtonClick,
                        toggleAutoplay: toggleAutoplay,
                        autoplayIsPlaying: autoplayIsPlaying,
                        slotClassNames: {
                            buttonClass,
                            dotsPaginationClass,
                            dotButtonClass,
                            dotClass,
                            blurBoxClass,
                            blurBoxDividerClass,
                            blurBoxNumbersClass,
                            blurBoxLightboxButtonClass,
                            blurBoxAutoplayButtonClass
                        },
                        variantSlots: {
                            prevButtonSlot,
                            nextButtonSlot,
                            hiddenButtonSlot,
                            dotsPaginationSlot,
                            dotButtonSlot
                        },
                        currentSlide: currentSlide,
                        totalSlides: totalSlides
                    })
                ]
            })
        })
    });
}
const Carousel_Carousel = Carousel;
export { Carousel, Carousel_Carousel as default };
